/**
 * The Channel Partner button in the dashboard's Forest hero (top-right, per
 * the product spec). It follows `/me`:
 *
 *   nothing yet / can re-apply → "Register as a Channel Partner" (+ info tip)
 *   invitation draft           → "Complete Channel Partner application"
 *   pending                    → "Channel Partner Approval Request Pending"
 *   rejected, wait running     → "Channel Partner application update"
 *   approved (`can_access_hub`)→ "Channel Partner Hub"
 *
 * Registering is never blocked here: the form explains itself when the
 * agreement is not published yet. Renders nothing while `/me` loads.
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight, Handshake, Hourglass, Info, MailPlus, XCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  buttonVariants,
  cn,
} from '@finmin/ui';
import { useChannelPartnerContext } from '@/api/channel-partners';
import { useNotifications } from '@/features/notifications/notifications-context';

const REGISTER_INFO =
  'Any individual, customer, professional, or business who can refer one or more clients to FinminOS may become a Channel Partner, subject to approval.';

/** Quiet pill for states that are informational rather than a call to action. */
const STATUS_PILL =
  'inline-flex items-center gap-2 rounded-md border border-cream/25 px-3 py-2 text-xs font-semibold text-cream transition-colors hover:bg-cream/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function ChannelPartnerHeroButton() {
  const { data: ctx } = useChannelPartnerContext();
  const { channelPartnerUnreadCount } = useNotifications();
  if (!ctx) return null;

  const status = ctx.application?.status;

  if (ctx.can_access_hub) {
    return (
      <Link
        to="/channel-partner/hub"
        className={cn(buttonVariants({ size: 'sm' }), 'relative bg-cream text-forest')}
      >
        <Handshake className="h-4 w-4" aria-hidden />
        Channel Partner Hub
        {channelPartnerUnreadCount > 0 && (
          <span
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-accent-foreground"
            aria-label={`${channelPartnerUnreadCount} unread Hub notifications`}
          >
            {channelPartnerUnreadCount > 9 ? '9+' : channelPartnerUnreadCount}
          </span>
        )}
      </Link>
    );
  }

  if (status === 'pending') {
    return (
      <Link to="/channel-partner" className={STATUS_PILL}>
        <Hourglass className="h-4 w-4 text-amber" aria-hidden />
        Channel Partner Approval Request Pending
      </Link>
    );
  }

  if (status === 'draft' && ctx.can_apply) {
    return (
      <Link to="/channel-partner/apply" className={buttonVariants({ size: 'sm' })}>
        <MailPlus className="h-4 w-4" aria-hidden />
        Complete Channel Partner application
      </Link>
    );
  }

  if (status === 'rejected' && !ctx.can_apply) {
    return (
      <Link to="/channel-partner" className={STATUS_PILL}>
        <XCircle className="h-4 w-4 text-amber" aria-hidden />
        Channel Partner application update
      </Link>
    );
  }

  if (!ctx.can_apply) return null;

  return (
    <div className="flex items-center gap-1.5">
      <Link
        // A rejected applicant who may re-apply sees the decision first.
        to={status === 'rejected' ? '/channel-partner' : '/channel-partner/apply'}
        className={buttonVariants({ size: 'sm' })}
      >
        Register as a Channel Partner
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={REGISTER_INFO}
              className="flex h-8 w-8 items-center justify-center rounded-md text-cream/80 transition-colors hover:bg-cream/10 hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="h-4 w-4" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
            {REGISTER_INFO}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
